/**
 * The glyph registry for the notifications feature — Lucide components, keyed by
 * their own Lucide export names.
 *
 * This feature used to inline raw Phosphor path data and draw its own `<svg>` so
 * it carried no icon library at runtime. That is no longer worth its cost: the
 * rest of the app already ships `lucide-react` on every screen the bell appears
 * on, so the geometry was being hand-maintained to avoid a dependency that was
 * loaded anyway — and a Phosphor glyph next to Lucide chrome was the one place
 * in the app drawn in a different hand.
 *
 * Keys are Lucide's export names verbatim, so there is no second vocabulary to
 * learn and no Phosphor-name -> Lucide-name lookup for a reader to hold. Adding
 * a glyph is: import it, add one line here, reference it from
 * `notification-visuals.ts`.
 *
 * The map exists rather than components being referenced directly because the
 * category table stays a table of plain strings — see `ICategoryVisual.icon`.
 * `NotificationIcon` is the only thing that turns a name into a component.
 */
import {
    AlarmClock,
    AtSign,
    BadgeQuestionMark,
    Bell,
    BookOpenText,
    Bot,
    Bug,
    CalendarCheck,
    CalendarOff,
    CalendarPlus,
    CalendarX,
    ChartColumn,
    Check,
    CheckCheck,
    CircleCheck,
    CirclePause,
    CirclePlay,
    CircleX,
    ClipboardList,
    CornerUpLeft,
    FileSpreadsheet,
    FileX,
    FlaskConical,
    Hand,
    Headset,
    LifeBuoy,
    Lightbulb,
    ListChecks,
    Lock,
    type LucideIcon,
    Mail,
    MapPinOff,
    Megaphone,
    MessageCircleMore,
    PenLine,
    PhoneMissed,
    Plug,
    RefreshCw,
    Send,
    Server,
    ShieldCheck,
    Timer,
    TriangleAlert,
    Trophy,
    Unlink,
    Unplug,
    UserCheck,
    UserPlus,
    Users,
    Voicemail,
    Workflow,
    X,
} from 'lucide-react';

export const NOTIFICATION_ICONS = {
    AlarmClock,
    AtSign,
    BadgeQuestionMark,
    Bell,
    BookOpenText,
    Bot,
    Bug,
    CalendarCheck,
    CalendarOff,
    CalendarPlus,
    CalendarX,
    ChartColumn,
    Check,
    CheckCheck,
    CircleCheck,
    CirclePause,
    CirclePlay,
    CircleX,
    ClipboardList,
    CornerUpLeft,
    FileSpreadsheet,
    FileX,
    FlaskConical,
    Hand,
    Headset,
    LifeBuoy,
    Lightbulb,
    ListChecks,
    Lock,
    Mail,
    MapPinOff,
    Megaphone,
    MessageCircleMore,
    PenLine,
    PhoneMissed,
    Plug,
    RefreshCw,
    Send,
    Server,
    ShieldCheck,
    Timer,
    TriangleAlert,
    Trophy,
    Unlink,
    Unplug,
    UserCheck,
    UserPlus,
    Users,
    Voicemail,
    Workflow,
    X,
} satisfies Record<string, LucideIcon>;

/** A glyph this feature can draw — any key of `NOTIFICATION_ICONS`. */
export type NotificationIconName = keyof typeof NOTIFICATION_ICONS;
